import type { ReactNode } from 'react';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyL, BrandL } from '#src/atoms/typography/index.tsx';
import { Bezel } from '#src/atoms/ui/bezels/Bezel/index.tsx';
import { NewSeasonLogo } from '#src/components/Season2026/NewSeasonLogo/index.tsx';
import { TextField } from '#src/components/form/Form/TextField/index.tsx';
import { NicknameForm } from '#src/components/form/NicknameForm/index.tsx';
import { EnterPromoCodeButton } from '#src/components/landing/EnterPromoCodeButton/index.tsx';
import { Picture } from '#src/components/landing/Picture/index.tsx';
import type { League } from '#src/components/landing/types.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const Wrapper = styled(Vertical)`
  position: relative;
  height: 100%;
  mask-image: linear-gradient(to bottom, black 90%, transparent);
  background: no-repeat center top / cover var(--background);
  @media ${tabletAndAbove} {
    padding-bottom: var(--quadruple-unit);
  }
`;

const BackgroundImage = styled(Picture)`
  img {
    position: absolute;
    z-index: -2;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const TextContainer = styled(Vertical)`
  z-index: 10;
  padding: var(--unit);
  @media ${tabletAndAbove} {
    padding: var(--triple-unit) 0 calc(var(--unit) * 8);
  }
`;

const StyledDescription = styled(BodyL)`
  text-align: center;
  line-height: 1.7;
  text-wrap: balance;
  @media ${tabletAndAbove} {
    max-width: 800px;
  }
`;

const StyledTitle = styled.h1`
  font: var(--t-brand-xxl);
  text-align: center;
  text-transform: uppercase;
  line-height: 1;

  @media ${tabletAndAbove} {
    font-size: var(--fs-landing-title);
  }
`;

const Players = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  width: 100%;
  mask-image: linear-gradient(to bottom, black 80%, transparent 100%);
  background: linear-gradient(to bottom, transparent 0%, black 50%);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 30%;
    pointer-events: none;
  }

  @media ${tabletAndAbove} {
    display: contents;
    background: none;
    mask-image: none;

    &::after {
      display: none;
    }
  }
`;

const PlayerImg = styled(Picture)`
  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
  &:first-child {
    z-index: 2;
    grid-area: 1 / 1 / 1 / 4;
  }
  &:last-child {
    z-index: 1;
    grid-area: 1 / 3 / 1 / 6;
  }
  @media ${tabletAndAbove} {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: block;
    min-width: 518px;
    &:first-child {
      right: calc(100% - var(--quadruple-unit));
    }
    &:last-child {
      left: calc(100% - var(--quadruple-unit));
    }
  }
`;

const FormWrapper = styled(Vertical)`
  position: relative;
  padding: var(--double-unit);
  width: calc(var(--unit) * 53);
  max-width: 100%;
  @media ${tabletAndAbove} {
    padding: 0;
  }
`;

const WalletWrapper = styled.div`
  align-self: center;
`;

const StyledTextField = styled(TextField)`
  --input-border-color: var(--c-nd-200);
  --input-bg-color: var(--c-nd-50-opaque);
`;

const BezelInnerWrapper = styled(Vertical)`
  flex: 1;
  padding: calc(var(--unit) * 5);
`;

const BezelTitle = styled(BrandL)`
  line-height: 1.2;
  text-align: center;
`;

const InputWrapper = styled.div`
  width: 100%;
`;

const StarBallIcon = styled(StarBall)`
  width: var(--double-unit);
  height: var(--double-unit);
`;

export type Player = {
  pictureUrl: string;
  height: number;
  width: number;
};

type Props = {
  assets: {
    background: string;
    players: Player[];
  };
  leagueLogo: ReactNode;
  league: League;
  messages: {
    hero: MessageDescriptor;
    heroWallet: MessageDescriptor;
    heroWalletPlaceholder: MessageDescriptor;
    heroDescription: MessageDescriptor;
  };
};

type WalletProps = {
  league: League;
  messages: {
    heroWallet: MessageDescriptor;
    heroWalletPlaceholder: MessageDescriptor;
  };
};

const Wallet = ({ league, messages }: WalletProps) => {
  const { signUp } = useConnectionContext();
  const [error, setError] = useState(false);
  const { formatMessage } = useIntl();
  const track = useEvents();

  return (
    <Bezel
      bezelSize={20}
      radius={10}
      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 3%, black 0) 0 0/5px 5px, radial-gradient(
      ellipse at center bottom,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0.5) 30%,
      rgba(255, 255, 255, 0) 100%
    )"
      borderColor="linear-gradient(to bottom, var(--c-nd-200) 90%, var(--c-white)"
    >
      <BezelInnerWrapper gap={3} center>
        <BezelTitle>
          <FormattedMessage {...messages.heroWallet} />
        </BezelTitle>
        <InputWrapper>
          <NicknameForm
            sport={Sport.FOOTBALL}
            onSuccess={({ nickname }) => {
              track('Landing Page Nickname Form', {
                status: 'success',
                source: league,
              });
              signUp(undefined, { step: nickname ? 2 : 1, nickname });
              setError(false);
            }}
            onError={() => {
              track('Landing Page Nickname Form', {
                status: 'error',
                source: league,
              });
              setError(true);
            }}
            render={(Error, SubmitButton) => (
              <Vertical gap={3}>
                <Vertical>
                  <StyledTextField
                    type="text"
                    name="nickname"
                    startAdornment={<StarBallIcon />}
                    placeholder={formatMessage(messages.heroWalletPlaceholder)}
                  />
                  {!!error && <Error />}
                  <EnterPromoCodeButton />
                </Vertical>
                <SubmitButton size="medium" color="secondary">
                  <FormattedMessage defaultMessage="Create your team" />
                </SubmitButton>
              </Vertical>
            )}
          />
        </InputWrapper>
      </BezelInnerWrapper>
    </Bezel>
  );
};

export const WalletLandingHero = ({
  assets,
  leagueLogo,
  league,
  messages,
}: Props) => {
  return (
    <Wrapper>
      <BackgroundImage picture={assets.background} alt="" />
      <TextContainer center gap={2}>
        {leagueLogo || <NewSeasonLogo gap="small" size="medium" iconSize={5} />}
        <StyledTitle>
          <FormattedMessage defaultMessage="Scout. Play. Win." />
        </StyledTitle>
        <StyledDescription>
          <FormattedMessage {...messages.heroDescription} />
        </StyledDescription>
      </TextContainer>
      <WalletWrapper>
        <FormWrapper center>
          <Wallet
            league={league}
            messages={{
              heroWallet: messages.heroWallet,
              heroWalletPlaceholder: messages.heroWalletPlaceholder,
            }}
          />
          <Players>
            <PlayerImg picture={assets.players[0].pictureUrl} alt="" />
            <PlayerImg picture={assets.players[1].pictureUrl} alt="" />
          </Players>
        </FormWrapper>
      </WalletWrapper>
    </Wrapper>
  );
};
